// ============================================================================
// GERENCIAMENTO DE CURSOS - DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/GerenciamentoCursosAdm.tsx
// UPDATED: 2026-04-07 — botão editar adicionado na tabela
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Filter, BookOpen, Edit, Eye, Tag, DollarSign, Users,
  Calendar, BarChart3, TrendingUp, Star, Building2, Download, Plus,
  CheckCircle, XCircle, Clock, Loader2, AlertTriangle
} from 'lucide-react';
import { API_BASE, authH } from '../../utils/apiConfig';
import ModalNovoCurso from './ModalNovoCurso';

// ─── Types ───────────────────────────────────────────────────────────────────
type CursoStatus = 'ativo' | 'inativo' | 'rascunho' | 'pendente' | 'rejeitado';

interface Curso {
  id: string;
  nome: string;
  instrutor: string;
  categoria: string;
  preco: number;
  alunos: number;
  alunosAtivos: number;
  status: CursoStatus;
  avaliacao: number;
  dataLancamento: string;
  afiliado: string;
  afiliadoId: string;
  cargaHoraria: number;
  modalidade: 'presencial' | 'online' | 'hibrido';
  certificados: number;
}

interface Stats {
  total: number;
  ativos: number;
  inativos: number;
  rascunhos: number;
  pendentes: number;
  totalAlunos: number;
  totalCertificados: number;
  receitaEstimada: number;
}

// ─── Mapper ──────────────────────────────────────────────────────────────────
const mapCursoStatus = (s: string): CursoStatus => {
  const m: Record<string, CursoStatus> = {
    active: 'ativo', approved: 'ativo', inactive: 'inativo',
    draft: 'rascunho', pending: 'pendente', rejected: 'rejeitado',
    ativo: 'ativo', inativo: 'inativo', rascunho: 'rascunho',
  };
  return m[s] ?? 'rascunho';
};

const mapCurso = (c: any): Curso => ({
  id: c.id,
  nome: c.courseName ?? c.nome ?? '',
  instrutor: c.instructorName ?? c.instrutor ?? '',
  categoria: c.category ?? c.categoria ?? '',
  preco: Number(c.price ?? c.preco ?? 0),
  alunos: c.studentsCount ?? c.alunos ?? 0,
  alunosAtivos: c.activeStudentsCount ?? c.alunosAtivos ?? 0,
  status: mapCursoStatus(c.status ?? ''),
  avaliacao: Number(c.rating ?? c.avaliacao ?? 0),
  dataLancamento: c.launchDate ?? c.createdAt ?? c.dataLancamento ?? '',
  afiliado: c.affiliate?.schoolName ?? c.affiliateName ?? c.afiliado ?? '',
  afiliadoId: c.affiliateId ?? c.afiliadoId ?? '',
  cargaHoraria: c.durationHours ?? c.cargaHoraria ?? 0,
  modalidade: c.modality ?? c.modalidade ?? 'presencial',
  certificados: c.certificatesCount ?? c.certificados ?? 0,
});

// ─── Component ───────────────────────────────────────────────────────────────
const GerenciamentoCursosAdm: React.FC = () => {
  const [cursos, setCursos]                   = useState<Curso[]>([]);
  const [stats, setStats]                     = useState<Stats | null>(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [busca, setBusca]                     = useState('');
  const [buscaInput, setBuscaInput]           = useState('');
  const [filtroStatus, setFiltroStatus]       = useState<string>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  const [filtroAfiliado, setFiltroAfiliado]   = useState<string>('todos');
  const [editandoPreco, setEditandoPreco]     = useState<string | null>(null);
  const [novoPreco, setNovoPreco]             = useState('');
  const [cursoSelecionado, setCursoSelecionado] = useState<Curso | null>(null);
  const [modalAberto, setModalAberto]         = useState(false);
  const [modalRejeitar, setModalRejeitar]     = useState<Curso | null>(null);
  const [motivoRejeicao, setMotivoRejeicao]   = useState('');
  const [salvando, setSalvando]               = useState(false);
  const [totalRegistros, setTotalRegistros]   = useState(0);
  const [modalNovoCurso, setModalNovoCurso]   = useState(false);
  const [modalEditar, setModalEditar]         = useState(false);
  const [formEditar, setFormEditar]           = useState<Record<string, any>>({});
  const [salvandoEdicao, setSalvandoEdicao]   = useState(false);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput), 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // ─── Fetch ───────────────────────────────────────────────────────────────
  const fetchCursos = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (filtroStatus !== 'todos')   params.set('status', filtroStatus);
      if (filtroAfiliado !== 'todos') params.set('affiliateId', filtroAfiliado);
      const res = await fetch(`${API_BASE}/courses?${params}`, { headers: authH() });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setCursos((data.courses ?? data.results ?? []).map(mapCurso));
      setTotalRegistros(data.total ?? 0);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar cursos');
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, filtroAfiliado]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/courses/stats/summary`, { headers: authH() });
      if (!res.ok) return;
      const d = await res.json();
      setStats({
        total:             d.total ?? d.totalCourses ?? 0,
        ativos:            d.active ?? d.approved ?? d.ativos ?? 0,
        inativos:          d.inactive ?? d.inativos ?? 0,
        rascunhos:         d.draft ?? d.rascunhos ?? 0,
        pendentes:         d.pending ?? d.pendentes ?? 0,
        totalAlunos:       d.totalStudents ?? d.totalAlunos ?? 0,
        totalCertificados: d.totalCertificates ?? d.totalCertificados ?? 0,
        receitaEstimada:   d.estimatedRevenue ?? d.receitaEstimada ?? 0,
      });
    } catch { /* opcional */ }
  }, []);

  useEffect(() => { fetchCursos(); }, [fetchCursos]);
  useEffect(() => { fetchStats();  }, [fetchStats]);

  // ─── Filtro local ─────────────────────────────────────────────────────────
  const cursosFiltrados = useMemo(() => {
    return cursos.filter(c => {
      const matchBusca = !busca ||
        c.nome.toLowerCase().includes(busca.toLowerCase()) ||
        c.instrutor.toLowerCase().includes(busca.toLowerCase()) ||
        c.afiliado.toLowerCase().includes(busca.toLowerCase());
      const matchCat = filtroCategoria === 'todas' || c.categoria === filtroCategoria;
      return matchBusca && matchCat;
    });
  }, [cursos, busca, filtroCategoria]);

  const categorias = useMemo(
    () => Array.from(new Set(cursos.map(c => c.categoria))).sort(), [cursos]);
  const afiliados = useMemo(
    () => Array.from(new Map(cursos.map(c => [c.afiliadoId, { id: c.afiliadoId, nome: c.afiliado }])).values()),
    [cursos]);

  const statsCalc = stats ?? {
    total:             totalRegistros,
    ativos:            cursos.filter(c => c.status === 'ativo').length,
    inativos:          cursos.filter(c => c.status === 'inativo').length,
    rascunhos:         cursos.filter(c => c.status === 'rascunho').length,
    pendentes:         cursos.filter(c => c.status === 'pendente').length,
    totalAlunos:       cursos.reduce((s, c) => s + c.alunos, 0),
    totalCertificados: cursos.reduce((s, c) => s + c.certificados, 0),
    receitaEstimada:   cursos.filter(c => c.status === 'ativo').reduce((s, c) => s + c.preco * c.alunosAtivos, 0),
  };

  // ─── Ações ────────────────────────────────────────────────────────────────
  const handleAprovar = async (id: string) => {
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/courses/${id}/approve`, { method: 'POST', headers: authH() });
      if (!res.ok) throw new Error();
      fetchCursos(); fetchStats();
      setModalAberto(false);
    } catch { alert('Erro ao aprovar curso'); }
    finally { setSalvando(false); }
  };

  const handleRejeitar = async () => {
    if (!modalRejeitar) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/courses/${modalRejeitar.id}/reject`, {
        method: 'POST', headers: authH(),
        body: JSON.stringify({ reason: motivoRejeicao || 'Reprovado pelo administrador' }),
      });
      if (!res.ok) throw new Error();
      setModalRejeitar(null); setMotivoRejeicao('');
      fetchCursos();
    } catch { alert('Erro ao rejeitar curso'); }
    finally { setSalvando(false); }
  };

  const handleSalvarPreco = async (id: string) => {
    const preco = novoPreco.trim() === '' ? 0 : parseFloat(novoPreco);
    if (isNaN(preco) || preco < 0) { alert('Preço inválido'); return; }
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/courses/${id}`, {
        method: 'PATCH', headers: authH(),
        body: JSON.stringify({ price: preco }),
      });
      if (!res.ok) throw new Error();
      setEditandoPreco(null); fetchCursos();
    } catch { alert('Erro ao salvar preço'); }
    finally { setSalvando(false); }
  };

  const handleSalvarEdicao = async () => {
    if (!cursoSelecionado) return;
    setSalvandoEdicao(true);
    try {
      const payload: Record<string, any> = {};
      if (formEditar.nome)          payload.courseName     = formEditar.nome;
      if (formEditar.instrutor)     payload.instructorName = formEditar.instrutor;
      if (formEditar.categoria)     payload.category       = formEditar.categoria;
      if (formEditar.modalidade)    payload.modality       = formEditar.modalidade;
      if (formEditar.cargaHoraria)  payload.durationHours  = Number(formEditar.cargaHoraria);
      if (formEditar.coverImageUrl) payload.coverImageUrl  = formEditar.coverImageUrl;
      if (formEditar.externalUrl)   payload.externalUrl    = formEditar.externalUrl;
      payload.price = formEditar.preco === '' ? 0 : Number(formEditar.preco);

      const res = await fetch(`${API_BASE}/courses/${cursoSelecionado.id}`, {
        method: 'PATCH', headers: authH(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      setModalEditar(false);
      fetchCursos();
    } catch { alert('Erro ao salvar edição'); }
    finally { setSalvandoEdicao(false); }
  };

  // ─── Badges ───────────────────────────────────────────────────────────────
  const getStatusBadge = (status: CursoStatus) => {
    const badges: Record<CursoStatus, { bg: string; text: string; border: string; label: string }> = {
      ativo:     { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: '● Ativo' },
      inativo:   { bg: 'bg-gray-100',  text: 'text-gray-800',  border: 'border-gray-300',  label: '○ Inativo' },
      rascunho:  { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', label: '◑ Rascunho' },
      pendente:  { bg: 'bg-blue-100',  text: 'text-blue-800',  border: 'border-blue-300',  label: '⏳ Pendente' },
      rejeitado: { bg: 'bg-red-100',   text: 'text-red-800',   border: 'border-red-300',   label: '✕ Rejeitado' },
    };
    const b = badges[status] ?? badges.rascunho;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${b.bg} ${b.text} ${b.border}`}>
        {b.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Gerenciamento de Cursos</h2>
            <p className="text-gray-600">Aprovação, precificação e gestão de cursos cadastrados</p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all">
              <Download className="w-5 h-5" /> Exportar
            </button>
            <button
              onClick={() => setModalNovoCurso(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold hover:shadow-lg transition-all">
              <Plus className="w-5 h-5" /> Novo Curso
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {[
            { label: 'Total',        value: statsCalc.total,             bg: 'bg-blue-50',    border: 'border-blue-200' },
            { label: 'Ativos',       value: statsCalc.ativos,            bg: 'bg-green-50',   border: 'border-green-200' },
            { label: 'Inativos',     value: statsCalc.inativos,          bg: 'bg-gray-50',    border: 'border-gray-200' },
            { label: 'Rascunhos',    value: statsCalc.rascunhos,         bg: 'bg-amber-50',   border: 'border-amber-200' },
            { label: 'Pendentes',    value: statsCalc.pendentes,         bg: 'bg-blue-50',    border: 'border-blue-200' },
            { label: 'Alunos',       value: statsCalc.totalAlunos,       bg: 'bg-purple-50',  border: 'border-purple-200' },
            { label: 'Certificados', value: statsCalc.totalCertificados, bg: 'bg-indigo-50',  border: 'border-indigo-200' },
            { label: 'Receita Est.', value: `R$${(statsCalc.receitaEstimada / 1000).toFixed(0)}k`, bg: 'bg-emerald-50', border: 'border-emerald-200' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4 border ${s.border}`}>
              <p className="text-sm text-gray-600 mb-1">{s.label}</p>
              <p className="text-xl font-black text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
              placeholder="Buscar por nome, instrutor..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold" />
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Status</option>
              <option value="approved">Ativos</option>
              <option value="pending">Pendentes</option>
              <option value="draft">Rascunhos</option>
              <option value="inactive">Inativos</option>
              <option value="rejected">Rejeitados</option>
            </select>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Tag className="w-5 h-5 text-gray-600" />
            <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todas">Todas Categorias</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Building2 className="w-5 h-5 text-gray-600" />
            <select value={filtroAfiliado} onChange={e => setFiltroAfiliado(e.target.value)}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Afiliados</option>
              {afiliados.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando cursos...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-bold">{error}</p>
            <button onClick={fetchCursos} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Curso', 'Categoria', 'Status', 'Preço', 'Alunos', 'Avaliação', 'Afiliado', ''].map(h => (
                      <th key={h} className={`px-6 py-4 text-sm font-black text-gray-900 ${h === '' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cursosFiltrados.map(curso => (
                    <tr key={curso.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            <BookOpen className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{curso.nome}</p>
                            <p className="text-sm text-gray-600">{curso.instrutor} · {curso.cargaHoraria}h · {curso.modalidade}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-300">
                          {curso.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(curso.status)}</td>
                      <td className="px-6 py-4">
                        {editandoPreco === curso.id ? (
                          <div className="flex items-center gap-2">
                            <input type="number" value={novoPreco} onChange={e => setNovoPreco(e.target.value)}
                              className="w-28 px-2 py-1 border-2 border-blue-500 rounded-lg text-sm font-bold" />
                            <button onClick={() => handleSalvarPreco(curso.id)} disabled={salvando}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs font-bold disabled:opacity-50">✓</button>
                            <button onClick={() => setEditandoPreco(null)} className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs font-bold">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">
                              {curso.preco > 0 ? `R$ ${curso.preco.toFixed(2)}` : 'Gratuito'}
                            </span>
                            <button onClick={() => { setEditandoPreco(curso.id); setNovoPreco(String(curso.preco)); }}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors">
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">{curso.alunos}</p>
                        <p className="text-xs text-gray-500">{curso.alunosAtivos} ativos</p>
                      </td>
                      <td className="px-6 py-4">
                        {curso.avaliacao > 0 ? (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-bold text-gray-900">{curso.avaliacao.toFixed(1)}</span>
                          </div>
                        ) : <span className="text-gray-400 text-sm">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-900">{curso.afiliado}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {curso.status === 'pendente' && (
                            <>
                              <button onClick={() => handleAprovar(curso.id)} disabled={salvando}
                                className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                                <CheckCircle className="w-3.5 h-3.5" /> Aprovar
                              </button>
                              <button onClick={() => { setModalRejeitar(curso); setMotivoRejeicao(''); }}
                                className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 flex items-center gap-1">
                                <XCircle className="w-3.5 h-3.5" /> Rejeitar
                              </button>
                            </>
                          )}
                          {/* ← BOTÃO EDITAR */}
                          <button
                            onClick={() => {
                              setCursoSelecionado(curso);
                              setFormEditar({
                                nome: curso.nome, instrutor: curso.instrutor,
                                categoria: curso.categoria, modalidade: curso.modalidade,
                                cargaHoraria: curso.cargaHoraria, preco: curso.preco,
                                coverImageUrl: '', externalUrl: '',
                              });
                              setModalEditar(true);
                            }}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button onClick={() => { setCursoSelecionado(curso); setModalAberto(true); }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {cursosFiltrados.length === 0 && (
              <div className="py-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">Nenhum curso encontrado</p>
              </div>
            )}

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                Mostrando <span className="font-bold text-gray-900">{cursosFiltrados.length}</span> de{' '}
                <span className="font-bold text-gray-900">{totalRegistros}</span> cursos
              </p>
            </div>
          </>
        )}
      </div>

      {/* MODAL DETALHES */}
      {modalAberto && cursoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-2xl font-black text-gray-900">Detalhes do Curso</h3>
              <button onClick={() => setModalAberto(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                {getStatusBadge(cursoSelecionado.status)}
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-300">
                  {cursoSelecionado.categoria}
                </span>
              </div>
              <h4 className="text-xl font-black text-gray-900">{cursoSelecionado.nome}</h4>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Instrutor',     value: cursoSelecionado.instrutor },
                  { label: 'Afiliado',      value: cursoSelecionado.afiliado },
                  { label: 'Preço',         value: cursoSelecionado.preco > 0 ? `R$ ${cursoSelecionado.preco.toFixed(2)}` : 'Gratuito' },
                  { label: 'Carga Horária', value: `${cursoSelecionado.cargaHoraria}h` },
                  { label: 'Modalidade',    value: cursoSelecionado.modalidade },
                  { label: 'Lançamento',    value: cursoSelecionado.dataLancamento ? new Date(cursoSelecionado.dataLancamento).toLocaleDateString('pt-BR') : '—' },
                  { label: 'Total Alunos',  value: String(cursoSelecionado.alunos) },
                  { label: 'Certificados',  value: String(cursoSelecionado.certificados) },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-sm text-gray-600 mb-1">{f.label}</p>
                    <p className="font-bold text-gray-900">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalAberto(false)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Fechar
              </button>
              {cursoSelecionado.status === 'pendente' && (
                <>
                  <button onClick={() => handleAprovar(cursoSelecionado.id)} disabled={salvando}
                    className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60">
                    {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />} Aprovar
                  </button>
                  <button onClick={() => { setModalRejeitar(cursoSelecionado); setModalAberto(false); }}
                    className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 flex items-center justify-center gap-2">
                    <XCircle className="w-5 h-5" /> Rejeitar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL REJEITAR */}
      {modalRejeitar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-black text-gray-900">Rejeitar Curso</h3>
              <p className="text-sm text-gray-600 mt-1">{modalRejeitar.nome}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">Motivo da Rejeição</label>
              <textarea value={motivoRejeicao} onChange={e => setMotivoRejeicao(e.target.value)} rows={4}
                placeholder="Descreva o motivo para rejeitar este curso..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-gray-900" />
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => { setModalRejeitar(null); setMotivoRejeicao(''); }}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleRejeitar} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />} Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR CURSO */}
      {modalEditar && cursoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-gray-900">Editar Curso</h3>
                <p className="text-sm text-gray-500 mt-0.5">{cursoSelecionado.nome}</p>
              </div>
              <button onClick={() => setModalEditar(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: 'Nome do Curso',                       key: 'nome',          type: 'text' },
                { label: 'Instrutor',                           key: 'instrutor',     type: 'text' },
                { label: 'Carga Horária (h)',                   key: 'cargaHoraria',  type: 'number' },
                { label: 'Preço (R$) — deixe 0 para gratuito', key: 'preco',         type: 'number' },
                { label: 'URL da Capa',                         key: 'coverImageUrl', type: 'url' },
                { label: 'Link Externo',                        key: 'externalUrl',   type: 'url' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-bold text-gray-700 mb-1">{f.label}</label>
                  <input
                    type={f.type}
                    value={formEditar[f.key] ?? ''}
                    onChange={e => setFormEditar((p: any) => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 text-gray-900"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Categoria</label>
                <select value={formEditar.categoria ?? ''} onChange={e => setFormEditar((p: any) => ({ ...p, categoria: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900">
                  {['Cabelo','Maquiagem','Estética','Unhas','Barba','Spa','Cosmetologia','Massoterapia','Outro'].map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Modalidade</label>
                <select value={formEditar.modalidade ?? 'presencial'} onChange={e => setFormEditar((p: any) => ({ ...p, modalidade: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900">
                  <option value="presencial">Presencial</option>
                  <option value="online">Online</option>
                  <option value="hibrido">Híbrido</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalEditar(false)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleSalvarEdicao} disabled={salvandoEdicao}
                className="flex-1 px-4 py-3 rounded-xl bg-amber-600 text-white font-bold hover:bg-amber-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvandoEdicao ? <Loader2 className="w-5 h-5 animate-spin"/> : <Edit className="w-5 h-5"/>}
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOVO CURSO */}
      {modalNovoCurso && (
        <ModalNovoCurso
          onClose={() => setModalNovoCurso(false)}
          onSuccess={() => { fetchCursos(); fetchStats(); }}
        />
      )}

    </div>
  );
};

export default GerenciamentoCursosAdm;